import { Query } from "@apollo/client/react/components";
/* eslint-disable react/display-name */
import { Box, BreadcrumbLink, Breadcrumbs, Col, Grid, Loader, RowWrap } from "@govlaunch/core";
import { EditorWithToolbars } from "@govlaunch/editor";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { convertFromRaw, EditorState } from "draft-js";
import noop from "lodash/fp/noop";
import uniqBy from "lodash/fp/uniqBy";
import Link from "next/link";
import React, { ReactElement } from "react";
import useIsPhone from "~/lib/hooks/useIsPhone";
import ReportQuery from "~/lib/queries/ReportQuery";
import { IGetReportQuery, IGetReportQueryVariables } from "~/lib/queries/__generated__/ReportQuery.generated";
import notUndefinedOrNull from "~/lib/utils/notUndefinedOrNull";
import { SigninModal } from "~/src/components/auth/SignInModal";
import { useSelfie } from "~/components/auth/Auth";
import { Margin } from "~/components/spacings";
import ReportDiscussion from "~/components/report/ReportDiscussion";
import ReportHead from "~/components/report/ReportHead";

interface IReportContainerProps {
  slug: string;
}

export default ({ slug }: IReportContainerProps): ReactElement => {
  return (
    <Query<IGetReportQuery, IGetReportQueryVariables>
      query={ReportQuery}
      variables={{
        slug,
      }}
    >
      {({ data, loading, refetch }) => {
        if (!data) {
          return null;
        }

        if (loading) {
          return (
            <div
              css={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100vh",
              }}
            >
              <Loader centered={true} margin="0 auto" />
            </div>
          );
        }

        const report = data.reportBySlug;
        if (!report) {
          return null;
        }

        return (
          <>
            <ReportHead report={report} />

            <div
              css={{
                width: "100%",
              }}
            >
              <Box p={[10, 20, 20, 40]} pt={[20, 20, 40, 40]} pb={[20, 20, 40, 40]}>
                <Report report={report} refetch={() => refetch()} />
              </Box>
            </div>
          </>
        );
      }}
    </Query>
  );
};

interface IReportProps {
  report: IGetReportQuery["reportBySlug"];
  refetch: () => void;
}

function Report({ report, refetch }: IReportProps): ReactElement | null {
  const isPhone = useIsPhone();
  const user = useSelfie();

  if (!report) {
    return null;
  }

  const governments = uniqBy("_id", report.governments);

  return (
    <div>
      <Grid>
        {user && (
          <RowWrap>
            <Col mx="auto" width={[1, 1, 1, 10 / 12]} mb={[20, 20, 20, 20]}>
              <Breadcrumbs>
                <Link href="/reports/[reportSlug]" as={`/reports/${report.slug}`} passHref={true} legacyBehavior={true}>
                  <BreadcrumbLink>Report</BreadcrumbLink>
                </Link>
              </Breadcrumbs>
            </Col>
          </RowWrap>
        )}

        <RowWrap>
          <Col mx="auto" width={[1, 1, 1, 10 / 12]}>
            <div
              css={{
                textAlign: "left",
              }}
            >
              <h1
                css={{
                  fontWeight: "bold",
                  margin: 0,
                  fontSize: isPhone ? 24 : 32,
                }}
              >
                {report.title}
              </h1>

              <p
                css={{
                  color: palette.text,
                  margin: 0,
                  marginTop: 10,
                }}
              >
                {report.description}
              </p>
            </div>
          </Col>
        </RowWrap>

        {governments.length > 0 && (
          <RowWrap>
            <Col mx="auto" width={[1, 1, 1, 10 / 12]} pt={[20, 20, 20, 20]}>
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  "& > a": {
                    marginRight: 10,
                  },
                }}
              >
                {governments
                  .slice(0, 5)
                  .filter(notUndefinedOrNull)
                  .map((government) => {
                    return (
                      <Link
                        key={government._id}
                        href="/governments/[governmentSlug]/[[...content]]"
                        as={`/governments/${government.slug}`}
                        passHref={true}
                        legacyBehavior={true}
                      >
                        <a
                          css={{
                            textDecoration: "none",
                          }}
                        >
                          <div
                            css={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <ProfilePicture
                              size={34}
                              name={government.name}
                              image={government.logo || ""}
                              innovator={government.isInnovator}
                            />

                            <Margin
                              ml={5}
                              css={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <span
                                css={{
                                  fontWeight: 500,
                                  fontSize: 14,
                                  color: palette.text,
                                }}
                              >
                                {government.name}
                              </span>

                              {!government.ownerless && (
                                <span
                                  css={{
                                    fontSize: 12,
                                    color: palette.darkSkyLightBlue,
                                    marginLeft: 5,
                                  }}
                                >
                                  <svg viewBox="0 0 16 16" width="1em" height="1em">
                                    <path
                                      d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z"
                                      fill="currentColor"
                                      fillRule="nonzero"
                                    />
                                  </svg>
                                </span>
                              )}
                            </Margin>
                          </div>
                        </a>
                      </Link>
                    );
                  })}
              </div>
            </Col>
          </RowWrap>
        )}

        {(report.images || []).length > 0 && (
          <RowWrap>
            <Col mx="auto" width={[1, 1, 1, 10 / 12]} pt={[20, 20, 20, 20]}>
              <div
                css={{
                  position: "relative",
                  height: isPhone ? 260 : 520,
                }}
              >
                <div
                  css={{
                    display: "flex",
                    minHeight: isPhone ? 200 : 430,
                    height: "100%",
                    flexDirection: "column",
                    overflow: "hidden",
                    padding: 12,
                    borderRadius: 6,
                    flex: "1 1 0%",
                    backgroundColor: palette.lightestGray,
                  }}
                >
                  <div
                    css={{
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {(report.images || [])
                      .slice(0, 1)
                      .filter(notUndefinedOrNull)
                      .map((image: string) => {
                        if (!user) {
                          const parts = image.split("/");
                          const handle = parts[parts.length - 1];

                          return (
                            <img
                              key={image}
                              src={`https://cdn.filestackcontent.com/blur=amount:10/pixelate=amount:40/${handle}`}
                              css={{
                                height: "100%",
                                width: "100%",
                                objectFit: "cover",
                                borderRadius: 6,
                              }}
                            />
                          );
                        }

                        return (
                          <img
                            key={image}
                            src={image}
                            css={{
                              height: "100%",
                              width: "100%",
                              objectFit: "cover",
                              borderRadius: 6,
                            }}
                          />
                        );
                      })}
                  </div>
                </div>
              </div>
            </Col>
          </RowWrap>
        )}

        <RowWrap>
          <Col mx="auto" width={[1, 1, 1, 10 / 12]} pt={[20, 20, 20, 20]}>
            {user ? (
              <div
                css={{
                  position: "relative",
                  backgroundColor: palette.white,
                  borderRadius: 4,
                  border: `solid 1px ${palette.lightestGray}`,
                  padding: 20,
                }}
              >
                <div
                  css={{
                    textShadow: user ? undefined : "0 0 5px rgba(0, 0, 0, 0.5)",
                    opacity: user ? undefined : 0.035,
                  }}
                >
                  <EditorWithToolbars
                    editorKey="editor"
                    editorState={EditorState.createWithContent(convertFromRaw(report.details))}
                    readOnly={true}
                    onChange={noop}
                  />
                </div>
              </div>
            ) : (
              <div
                css={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <SigninModal isOpen={true} onSignIn={() => refetch()} onRequestClose={() => {}} />
              </div>
            )}
          </Col>
        </RowWrap>

        <RowWrap>
          <Col mx="auto" width={[1, 1, 1, 10 / 12]} pt={[20, 20, 20, 20]}>
            <ReportDiscussion report={report} />
          </Col>
        </RowWrap>
      </Grid>
    </div>
  );
}
